import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

type PaginationProps = {
    totalPages: number;
    currentPage: number;
    onPageChange: (page: number) => void;
};

export const TablePagination = ({ totalPages, currentPage, onPageChange }: PaginationProps) => {
    const [pageIndex, setPageIndex] = useState<number>(currentPage);

    const getVisiblePages = (page: number, total: number) => {
        if (total < 7) {
            return Array.from(Array(total).keys()).map((i) => i + 1);
        } else {
            if (page % 5 >= 0 && page > 4 && page + 2 < total) {
                return [1, page - 1, page, page + 1, total];
            } else if (page % 5 >= 0 && page > 4 && page + 2 >= total) {
                return [1, total - 3, total - 2, total - 1, total];
            } else {
                return [1, 2, 3, 4, 5, total];
            }
        }
    };

    const changePage = (page: number) => {
        const activePage = pageIndex + 1;

        if (page === activePage) {
            return;
        }

        const visiblePages = getVisiblePages(page, totalPages);
        setVisiblePages(visiblePages);
        setPageIndex(page - 1);
        onPageChange(page);
    };

    const [visiblePages, setVisiblePages] = useState<number[]>(getVisiblePages(0, totalPages));
    const activePage: number = pageIndex + 1;

    return (
        <div className='d-lg-flex align-items-center text-center mb-1 mt-3'>
            <span className='me-3'>
                Page{' '}
                <strong>
                    {pageIndex + 1} of {totalPages}
                </strong>{' '}
            </span>

            <ul className='pagination pagination-rounded d-inline-flex ms-auto align-item-center mb-0'>
                <li
                    key='prevpage'
                    className={classNames('page-item', 'paginate_button', 'previous', {
                        disabled: activePage === 1,
                    })}
                    onClick={() => {
                        if (activePage === 1) return;
                        changePage(activePage - 1);
                    }}>
                    <Link to='#' className='page-link'>
                        <i className='mdi mdi-chevron-left'></i>
                    </Link>
                </li>
                {(visiblePages || []).map((page, index, array) => {
                    return array[index - 1] + 1 < page ? (
                        <React.Fragment key={page.toString()}>
                            <li className='page-item disabled d-none d-xl-inline-block'>
                                <Link to='#' className='page-link'>
                                    ...
                                </Link>
                            </li>
                            <li
                                className={classNames('page-item', 'd-none', 'd-xl-inline-block', {
                                    active: activePage === page,
                                })}
                                onClick={(e) => changePage(page)}>
                                <Link to='#' className='page-link'>
                                    {page}
                                </Link>
                            </li>
                        </React.Fragment>
                    ) : (
                        <li
                            key={page.toString()}
                            className={classNames('page-item', 'd-none', 'd-xl-inline-block', {
                                active: activePage === page,
                            })}
                            onClick={(e) => changePage(page)}>
                            <Link to='#' className='page-link'>
                                {page}
                            </Link>
                        </li>
                    );
                })}
                <li
                    key='nextpage'
                    className={classNames('page-item', 'paginate_button', 'next', {
                        disabled: activePage === totalPages,
                    })}
                    onClick={() => {
                        if (activePage === totalPages) return;
                        changePage(activePage + 1);
                    }}>
                    <Link to='#' className='page-link'>
                        <i className='mdi mdi-chevron-right'></i>
                    </Link>
                </li>
            </ul>
        </div>
    );
};
